"use client";

import { motion } from "framer-motion";
import {
  ArrowRightLeft,
  Hand,
  MessageCircle,
  MessageSquare,
  Phone,
  PhoneForwarded,
  Mail,
  Users,
} from "lucide-react";
import { SiWhatsapp, SiFacebook } from "react-icons/si";
import { TextLinkCTA } from "~/components/siteglue/siteglue-cta";
import { AI_BUILT_FOR_HUMANS_LINE } from "~/lib/siteglue/ai-employee-plans";
import {
  HITL_PUBLIC_ROUTES,
  HITL_TERMS,
} from "~/lib/siteglue/hitl-terminology";
import { SITEGLUE_COLORS } from "~/lib/siteglue/marketing-chrome";

const HANDOFF_CHANNELS = [
  { label: "Website Chat", icon: MessageSquare },
  { label: "Phone", icon: Phone },
  { label: "SMS", icon: MessageCircle },
  { label: "Email", icon: Mail },
  { label: "WhatsApp", icon: SiWhatsapp },
  { label: "Messenger", icon: SiFacebook },
] as const;

const TAKEOVER_EXAMPLES = [
  { from: "Phone", to: "Call Transfer", icon: PhoneForwarded },
  { from: "Chat", to: "Live Chat", icon: MessageSquare },
  { from: "SMS", to: "Continue Messaging", icon: MessageCircle },
  { from: "Email", to: "Continue Reply", icon: Mail },
  { from: "WhatsApp", to: null, icon: SiWhatsapp },
  { from: "Messenger", to: null, icon: SiFacebook },
] as const;

function HitlIllustration() {
  return (
    <div
      className="relative mx-auto mt-6 flex h-28 w-full max-w-[220px] items-center justify-center"
      aria-hidden
    >
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#4f81bd]/10 via-white to-[#9cbb5a]/15" />
      <div className="relative flex items-center gap-3">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-[#4f81bd]/25 bg-white shadow-sm">
          <span className="text-lg font-semibold text-[#4f81bd]">AI</span>
        </div>
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#9cbb5a]/15 text-[#9cbb5a]">
          <Hand className="h-4 w-4" strokeWidth={2} />
        </div>
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
          <Users className="h-6 w-6 text-slate-700" strokeWidth={1.75} />
        </div>
      </div>
    </div>
  );
}

export function FeaturesHitlTeamSection() {
  return (
    <section
      id="human-in-the-loop"
      data-testid="section-how-siteglue-works-with-team"
      className="relative scroll-mt-28 border-b border-slate-200/80 bg-[#fafbfc] py-20 sm:py-28"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute left-1/2 top-0 h-[420px] w-[720px] -translate-x-1/2 rounded-full opacity-60 blur-[100px]"
          style={{
            background: `radial-gradient(ellipse, ${SITEGLUE_COLORS.blue}22, transparent 70%)`,
          }}
        />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-[#4f81bd]">
            {AI_BUILT_FOR_HUMANS_LINE}
          </p>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
          >
            How SiteGlue Works With Your Team
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="mt-4 text-lg leading-relaxed text-slate-600"
          >
            Your AI Employee handles the routine. Your people stay in control — with full context —
            whenever a conversation needs a human.
          </motion.p>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="flex flex-col rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-[#4f81bd]/10 text-[#4f81bd]">
              <ArrowRightLeft className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <h3 className="text-xl font-semibold tracking-tight text-slate-900">
              {HITL_TERMS.teamHandoff}
            </h3>
            <p className="mt-3 flex-1 text-[15px] leading-relaxed text-slate-600">
              When your AI Employee reaches an approved handoff condition, it collects context and
              routes the conversation to the right person.
            </p>
            <div className="mt-6 border-t border-slate-100 pt-5">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                Supported channels
              </p>
              <ul className="flex flex-wrap gap-2">
                {HANDOFF_CHANNELS.map(({ label, icon: Icon }) => (
                  <li
                    key={label}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700"
                  >
                    <Icon className="h-3.5 w-3.5 text-[#4f81bd]" aria-hidden />
                    {label}
                  </li>
                ))}
              </ul>
              <div className="mt-4">
                <TextLinkCTA
                  action="discoverPlatform"
                  location="feature_section"
                  href={HITL_PUBLIC_ROUTES.teamHandoff}
                  label={`Explore ${HITL_TERMS.teamHandoff}`}
                />
              </div>
            </div>
          </motion.article>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="flex flex-col rounded-2xl border border-[#4f81bd]/30 bg-white p-8 shadow-sm ring-1 ring-[#4f81bd]/10 transition-shadow hover:shadow-md"
          >
            <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-[#9cbb5a]/15 text-[#6b8f2e]">
              <Hand className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <h3 className="text-xl font-semibold tracking-tight text-slate-900">
              {HITL_TERMS.hitl}
            </h3>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
              Review, monitor, guide, and approve how your AI Employee works.
            </p>
            <HitlIllustration />
            <p className="mt-4 text-center text-xs font-medium text-slate-500">
              AI and humans working together — not replacing each other.
            </p>
            <div className="mt-4 text-center">
              <TextLinkCTA
                action="discoverPlatform"
                location="feature_section"
                href={HITL_PUBLIC_ROUTES.hitl}
                label={`Explore ${HITL_TERMS.hitl}`}
              />
            </div>
          </motion.article>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="flex flex-col rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-[#4f81bd]/10 text-[#4f81bd]">
              <Users className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <h3 className="text-xl font-semibold tracking-tight text-slate-900">
              {HITL_TERMS.teamTakeover}
            </h3>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
              Your staff can continue any supported conversation without losing available context.
            </p>
            <ul className="mt-6 space-y-2.5 border-t border-slate-100 pt-5">
              {TAKEOVER_EXAMPLES.map(({ from, to, icon: Icon }) => (
                <li key={from} className="flex items-center gap-2 text-sm text-slate-700">
                  <Icon className="h-4 w-4 shrink-0 text-[#4f81bd]" aria-hidden />
                  <span className="font-medium">{from}</span>
                  {to ? (
                    <>
                      <span className="text-slate-300" aria-hidden>
                        →
                      </span>
                      <span className="text-slate-600">{to}</span>
                    </>
                  ) : null}
                </li>
              ))}
            </ul>
            <div className="mt-4">
              <TextLinkCTA
                action="discoverPlatform"
                location="feature_section"
                href={HITL_PUBLIC_ROUTES.teamTakeover}
                label={`Explore ${HITL_TERMS.teamTakeover}`}
              />
            </div>
          </motion.article>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          <TextLinkCTA
            action="discoverPlatform"
            location="feature_section"
            href={HITL_PUBLIC_ROUTES.phoneRules}
            label={`Explore ${HITL_TERMS.phoneRules}`}
          />
          <TextLinkCTA
            action="discoverPlatform"
            location="feature_section"
            href={HITL_PUBLIC_ROUTES.hitl}
            label={`Explore ${HITL_TERMS.hitl}`}
          />
        </div>
      </div>
    </section>
  );
}
